import { ViewIcon, ViewOffIcon } from "@chakra-ui/icons"
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Img,
  Input,
  InputGroup,
  InputRightElement,
  Tag,
  TagCloseButton,
  TagLabel,
  type FlexProps
} from "@chakra-ui/react"
import React, { useState } from "react"
import CreatableSelect from "react-select/creatable"
import penIcon from "../../assets/images/penIcon.png"
import { uploadFile } from "../../pages/login/userProfile/userprofile.support.logo"
import { Colors } from "../../theme/colors"
import { type FormElementInterface } from "./commonTypes"
import { isTruthy } from "./utils"

export const formBoxStyleProps: FlexProps = {
  align: "center",
  flexDir: "column"
}

export const FormElement = ({
  type,
  placeHolder,
  label,
  requiredText,
  name,
  htmlValidation,
  inputStyles,
  formControlProps,
  value,
  onChangeCallBack,
  formLabelOptionalTextProps,
  formLabelTextProps,
  optionalLabel,
  customSelectOptions,
  defaultCustomSelectValue,
  showEditIcon,
  loginPage
}: FormElementInterface): JSX.Element => {
  const [viewPassword, setViewPassword] = useState<boolean>(false)
  const [formElementType, setFormElementType] = useState<FormElementInterface["type"]>(type)
  const [, setFileLoading] = useState<boolean>(false)
  const [fileURL, setFileURL] = useState<string>(value ?? "")
  const [error, setError] = useState<string>("")

  interface response {
    cdn_url: string
  }

  const handleFileUpload = async (file: File): Promise<void> => {
    setFileLoading(true)
    try {
      const formData = new FormData()
      formData.append("doc", file)
      const response = await uploadFile(formData)
      const data = response as response
      setFileURL(data.cdn_url)
      onChangeCallBack(data.cdn_url)
      setError("")
    } catch (err) {
      setError("Failed to upload file. Please try again.")
      console.error("File upload error:", err)
    } finally {
      setFileLoading(false)
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const element = e.target as HTMLInputElement
    const value = element?.value ?? ""
    onChangeCallBack(value)
    setError("") // Clear error when user starts typing
  }

  const handleSelectChange = (option: any): void => {
    onChangeCallBack(option?.value as string)
    setError("") // Clear error when user makes selection
  }

  const handlePasswordToggle = (): void => {
    setViewPassword(!viewPassword)
    if (viewPassword) {
      setFormElementType("password")
    }
    if (!viewPassword) {
      setFormElementType("text")
    }
  }

  const inputId = `form-input-${name}`
  const labelId = `form-label-${name}`
  const errorId = `form-error-${name}`
  const helpTextId = `form-help-${name}`

  return (
    <FormControl mx="0px" mt="0" mb="16px" width="100%" p="0" px="4px" isInvalid={!!error} {...formControlProps}>
      {isTruthy(label) && (
        <FormLabel
          id={labelId}
          htmlFor={inputId}
          fontSize={"16px"}
          fontFamily="PT Sans"
          lineHeight={"24px"}
          fontWeight="400"
          mb={["6px", "6px", "8px", "8px"]}
          mr="0"
          color={"white"}
        >
          <Flex justifyContent="space-beween" w="100%">
            <Box w="100%" {...formLabelTextProps} fontSize={"16px"} lineHeight={"24px"}>
              {htmlValidation?.required && <sup style={{ fontSize: "20px", top: "1px" }}>{requiredText}</sup>}
              {label}
            </Box>
            {!htmlValidation?.required && (
              <Box fontStyle="italic" fontSize="15px" {...formLabelOptionalTextProps}>
                {optionalLabel ?? "(optional)"}
              </Box>
            )}
          </Flex>
        </FormLabel>
      )}
      {type === "customSelect" && defaultCustomSelectValue?.value != null && (
        <Flex pos={"relative"}>
          <Box width="100%">
            <CreatableSelect
              placeholder={placeHolder ?? "Select an Option"}
              isClearable
              defaultValue={defaultCustomSelectValue}
              name={name}
              inputId={inputId}
              aria-labelledby={labelId}
              aria-describedby={error ? errorId : helpTextId}
              onChange={handleSelectChange}
              styles={{
                group: base => ({
                  ...base,
                  textAlign: "center",
                  fontSize: "14px"
                }),
                groupHeading: base => ({
                  ...base,
                  textTransform: "capitalize",
                  fontSize: "14px",
                  color: Colors.Primary.c1,
                  textAlign: "center",
                  fontWeight: "bold"
                }),
                control: (base, state) => ({
                  ...base,
                  borderColor: error ? Colors.Primary.c1 : state.isFocused ? Colors.Primary.accent : base.borderColor,
                  boxShadow: state.isFocused ? `0 0 0 1px ${Colors.Primary.accent}` : base.boxShadow
                })
              }}
              options={customSelectOptions}
            />
          </Box>

          {showEditIcon && <Img src={penIcon} h="19px" right="-25px" top="2" pos="absolute" alt="Edit" />}
        </Flex>
      )}
      {/* Signup form */}
      {type === "customSelect" && defaultCustomSelectValue?.value == null && (
        <CreatableSelect
          placeholder={placeHolder ?? "Select an Option"}
          isClearable
          name={name}
          inputId={inputId}
          aria-labelledby={labelId}
          aria-describedby={error ? errorId : helpTextId}
          onChange={handleSelectChange}
          options={customSelectOptions}
          styles={{
            control: (base, state) => ({
              ...base,
              textAlign: "center",
              fontSize: "14px",
              borderColor: error ? Colors.Primary.c1 : state.isFocused ? Colors.Primary.accent : base.borderColor,
              boxShadow: state.isFocused ? `0 0 0 1px ${Colors.Primary.accent}` : base.boxShadow
            }),
            group: base => ({
              ...base,
              textAlign: "center",
              fontSize: "14px"
            }),
            groupHeading: base => ({
              ...base,
              textTransform: "capitalize",
              fontSize: "14px",
              color: Colors.Primary.c1,
              textAlign: "center",
              fontWeight: "bold"
            })
          }}
          {...inputStyles}
        />
      )}

      {type === "fileUpload" && (
        <Flex pos={"relative"}>
          <Box width="100%">
            <Input
              type="file"
              id={inputId}
              aria-labelledby={labelId}
              aria-describedby={error ? errorId : helpTextId}
              onChange={e => {
                const file = e.target.files?.[0]
                if (file) {
                  void handleFileUpload(file)
                }
              }}
              accept="image/*"
              {...inputStyles}
            />
            {fileURL && (
              <Flex mt="2" align="center">
                <Img src={fileURL} height="50px" maxW="100px" mr="1" alt="Uploaded file" />
                <Tag size="sm" colorScheme="blue" borderRadius="full">
                  <TagLabel>File uploaded</TagLabel>
                  <TagCloseButton
                    onClick={() => {
                      setFileURL("")
                      onChangeCallBack("")
                    }}
                    aria-label="Remove uploaded file"
                  />
                </Tag>
              </Flex>
            )}
          </Box>
          {showEditIcon && <Img src={penIcon} h="19px" right="-25px" top="2" pos="absolute" alt="Edit" />}
        </Flex>
      )}

      {type !== "customSelect" && type !== "fileUpload" && (
        <Flex position={"relative"}>
          <InputGroup>
            <Input
              id={inputId}
              name={name}
              h="41px"
              w="254px"
              bg="white"
              borderRadius="2px"
              autoCapitalize="none"
              autoCorrect="off"
              borderWidth={0}
              type={formElementType}
              placeholder={placeHolder ?? ""}
              value={value ?? ""}
              aria-labelledby={labelId}
              aria-describedby={error ? errorId : helpTextId}
              aria-invalid={!!error}
              onChange={handleInputChange}
              {...htmlValidation}
              {...inputStyles}
            />
            {type === "password" && (
              <InputRightElement onClick={handlePasswordToggle} pr={loginPage ? "8" : 0}>
                <Button
                  variant="ghost"
                  size="sm"
                  bg="transparent"
                  _hover={{ bg: "transparent" }}
                  _active={{ bg: "transparent" }}
                  aria-label={viewPassword ? "Hide password" : "Show password"}
                  onClick={handlePasswordToggle}
                  _focus={{
                    outline: "2px solid",
                    outlineColor: Colors.Primary.accent,
                    outlineOffset: "2px"
                  }}
                >
                  {viewPassword && <ViewIcon />}
                  {!viewPassword && <ViewOffIcon />}
                </Button>
              </InputRightElement>
            )}
          </InputGroup>
          {showEditIcon && <Img src={penIcon} h="19px" right="-25px" top="2" pos="absolute" alt="Edit" />}
        </Flex>
      )}

      {error && (
        <FormErrorMessage id={errorId} role="alert">
          {error}
        </FormErrorMessage>
      )}

      {!error && htmlValidation?.required && (
        <FormHelperText id={helpTextId} color={Colors.SecondaryPrimary.c2}>
          This field is required
        </FormHelperText>
      )}
    </FormControl>
  )
}
